import {apiClient} from "./api-client";
import type {components} from "./api-types";

// ============================================================================
// Types - re-exported from generated OpenAPI types
// ============================================================================

export type AiPersona = components["schemas"]["AiPersona"];
export type AiPersonaGenerationRequest = components["schemas"]["AiPersonaGenerationRequest"];
export type AiPersonaGenerationResponse = components["schemas"]["AiPersonaGenerationResponse"];
export type EventContext = components["schemas"]["EventContext"];
export type SupportingMention = components["schemas"]["SupportingMention"];
export type ChatMessage = components["schemas"]["Message"];
export type SocialMediaPlatform = components["schemas"]["SocialMediaPlatform"];
export type MentionlyticsDateFilterResponse =
  components["schemas"]["MentionlyticsDateFilterResponse"];

// Request/Response types for chat
export type ChatProfileRequest = components["schemas"]["ChatRequest"];
export type ChatProfileResponse = components["schemas"]["ChatResponse"];

// Saved AI Persona types (for pinned/saved personas)
export type AiPersonaResponse = components["schemas"]["AiPersonaResponse"];
export type AiPersonaCreate = components["schemas"]["AiPersonaCreate"];
export type AiPersonaUpdate = components["schemas"]["AiPersonaUpdate"];
export type AiPersonaListResponse = components["schemas"]["AiPersonaListResponse"];

// Chat message types
export type AiPersonaChatMessageCreate = components["schemas"]["AiPersonaChatMessageCreate"];
export type AiPersonaChatMessageResponse = components["schemas"]["AiPersonaChatMessageResponse"];
export type AiPersonaChatMessagesResponse = components["schemas"]["AiPersonaChatMessagesResponse"];

// Location type (backend returns unknown, so we define it manually for now)
export interface Location {
  id: string;
  name: string;
  type: string;
}

// Platform type alias for backward compatibility
export type Platform = SocialMediaPlatform;

// ============================================================================
// API Functions
// ============================================================================

export async function generatePersonas(
  request: AiPersonaGenerationRequest
): Promise<AiPersonaGenerationResponse> {
  const {data, error} = await apiClient.POST("/api/ai-personas/generate", {
    body: request,
  });

  if (error || !data) {
    throw new Error((error as {detail?: string})?.detail || "Failed to generate personas");
  }

  return data;
}

export async function chatWithAiPersona(
  request: ChatProfileRequest,
  signal?: AbortSignal
): Promise<ChatProfileResponse> {
  const {data, error} = await apiClient.POST("/api/chat/profile", {
    body: request,
    signal,
  });

  if (error || !data) {
    throw new Error((error as {detail?: string})?.detail || "Failed to chat with ai persona");
  }

  return data;
}

export async function fetchLocations(): Promise<Location[]> {
  const {data, error} = await apiClient.GET("/api/entertainment/locations");

  if (error || !data) {
    throw new Error((error as {detail?: string})?.detail || "Failed to fetch locations");
  }

  // Handle both array response and wrapped response
  const responseData = data as {locations?: Location[]} | Location[] | unknown;
  if (Array.isArray(responseData)) {
    return responseData;
  }
  if (responseData && typeof responseData === "object" && "locations" in responseData) {
    return (responseData as {locations: Location[]}).locations;
  }
  return [];
}

export async function fetchPlatforms(): Promise<Platform[]> {
  const {data, error} = await apiClient.GET("/api/osint/platforms");

  if (error || !data) {
    throw new Error((error as {detail?: string})?.detail || "Failed to fetch platforms");
  }

  // Handle both array response and wrapped response
  if (Array.isArray(data)) {
    return data;
  }
  if (typeof data === "object" && "platforms" in data) {
    return (data as {platforms: Platform[]}).platforms;
  }
  return [];
}

// ============================================================================
// Saved AI Persona API Functions
// ============================================================================

export async function listSavedAiPersonas(): Promise<AiPersonaListResponse> {
  const {data, error} = await apiClient.GET("/api/ai-personas");

  if (error || !data) {
    throw new Error((error as {detail?: string})?.detail || "Failed to fetch saved personas");
  }

  return data;
}

export async function saveAiPersona(request: AiPersonaCreate): Promise<AiPersonaResponse> {
  const {data, error} = await apiClient.POST("/api/ai-personas", {
    body: request,
  });

  if (error || !data) {
    throw new Error((error as {detail?: string})?.detail || "Failed to save persona");
  }

  return data;
}

export async function getAiPersona(id: string): Promise<AiPersonaResponse> {
  const {data, error} = await apiClient.GET("/api/ai-personas/{persona_id}", {
    params: {
      path: {persona_id: id},
    },
  });

  if (error || !data) {
    throw new Error((error as {detail?: string})?.detail || "Failed to fetch persona");
  }

  return data;
}

export async function updateAiPersona(
  id: string,
  request: AiPersonaUpdate
): Promise<AiPersonaResponse> {
  const {data, error} = await apiClient.PATCH("/api/ai-personas/{persona_id}", {
    params: {
      path: {persona_id: id},
    },
    body: request,
  });

  if (error || !data) {
    throw new Error((error as {detail?: string})?.detail || "Failed to update persona");
  }

  return data;
}

export async function deleteAiPersona(id: string): Promise<void> {
  const {error} = await apiClient.DELETE("/api/ai-personas/{persona_id}", {
    params: {
      path: {persona_id: id},
    },
  });

  if (error) {
    throw new Error((error as {detail?: string})?.detail || "Failed to delete persona");
  }
}

// ============================================================================
// AI Persona Chat Message API Functions
// ============================================================================

export async function getAiPersonaMessages(
  personaId: string,
  skip?: number,
  limit?: number
): Promise<AiPersonaChatMessagesResponse> {
  const {data, error} = await apiClient.GET("/api/ai-personas/{persona_id}/messages", {
    params: {
      path: {persona_id: personaId},
      query: {skip, limit},
    },
  });

  if (error || !data) {
    throw new Error((error as {detail?: string})?.detail || "Failed to fetch chat messages");
  }

  return data;
}

export async function addAiPersonaMessage(
  personaId: string,
  message: AiPersonaChatMessageCreate
): Promise<AiPersonaChatMessageResponse> {
  const {data, error} = await apiClient.POST("/api/ai-personas/{persona_id}/messages", {
    params: {
      path: {persona_id: personaId},
    },
    body: message,
  });

  if (error || !data) {
    throw new Error((error as {detail?: string})?.detail || "Failed to add chat message");
  }

  return data;
}

export async function clearAiPersonaMessages(personaId: string): Promise<void> {
  const {error} = await apiClient.DELETE("/api/ai-personas/{persona_id}/messages", {
    params: {
      path: {persona_id: personaId},
    },
  });

  if (error) {
    throw new Error((error as {detail?: string})?.detail || "Failed to clear chat messages");
  }
}

// ============================================================================
// Utility Types
// ============================================================================

export type Psychotype =
  | "Innovator"
  | "Explorer"
  | "Pragmatist"
  | "Traditionalist"
  | "Influencer"
  | "Socializer"
  | "Analyst";

interface PsychotypeStyle {
  badge: string;
  gradient: string;
  ringHover: string;
}

const PSYCHOTYPE_STYLES: Record<Psychotype, PsychotypeStyle> = {
  Innovator: {
    badge: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    gradient: "from-purple-500/10 via-transparent",
    ringHover: "group-hover:ring-purple-500/30",
  },
  Explorer: {
    badge: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    gradient: "from-blue-500/10 via-transparent",
    ringHover: "group-hover:ring-blue-500/30",
  },
  Pragmatist: {
    badge: "bg-green-500/20 text-green-400 border-green-500/30",
    gradient: "from-green-500/10 via-transparent",
    ringHover: "group-hover:ring-green-500/30",
  },
  Traditionalist: {
    badge: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    gradient: "from-orange-500/10 via-transparent",
    ringHover: "group-hover:ring-orange-500/30",
  },
  Influencer: {
    badge: "bg-pink-500/20 text-pink-400 border-pink-500/30",
    gradient: "from-pink-500/10 via-transparent",
    ringHover: "group-hover:ring-pink-500/30",
  },
  Socializer: {
    badge: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    gradient: "from-yellow-500/10 via-transparent",
    ringHover: "group-hover:ring-yellow-500/30",
  },
  Analyst: {
    badge: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
    gradient: "from-cyan-500/10 via-transparent",
    ringHover: "group-hover:ring-cyan-500/30",
  },
};

const DEFAULT_PSYCHOTYPE_STYLE: PsychotypeStyle = {
  badge: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  gradient: "from-gray-500/10 via-transparent",
  ringHover: "group-hover:ring-gray-500/30",
};

export function getPsychotypeStyle(psychotype: string): PsychotypeStyle {
  return PSYCHOTYPE_STYLES[psychotype as Psychotype] ?? DEFAULT_PSYCHOTYPE_STYLE;
}

export function getPsychotypeColor(psychotype: string): string {
  return getPsychotypeStyle(psychotype).badge;
}
